"use client"

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react"

import {
  CONSENT_COOKIE_NAME,
  ConsentCategories,
  DEFAULT_ACCEPTED,
  DEFAULT_REJECTED,
  parseConsentCookie,
  serializeConsentCookie,
} from "./parse-cookie"

export type { ConsentCategories } from "./parse-cookie"
export { DEFAULT_ACCEPTED, DEFAULT_REJECTED } from "./parse-cookie"

type CookieConsentContextValue = {
  // null until the visitor has chosen; banner uses this to decide whether to render
  consent: ConsentCategories | null
  hasDecided: boolean
  acceptAll: () => void
  rejectAll: () => void
  savePreferences: (next: Pick<ConsentCategories, "analytics" | "marketing">) => void
  openPreferences: () => void
  closePreferences: () => void
  isPreferencesOpen: boolean
}

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null)

// Re-read the cookie from document.cookie on the client. Used as a fallback
// when no SSR-side initial value was provided (shouldn't happen in normal
// use because RootLayout reads it, but defensive in case the provider is
// instantiated outside that path).
function readCookieClient(): ConsentCategories | null {
  if (typeof document === "undefined") return null
  const match = document.cookie
    .split("; ")
    .find((c) => c.startsWith(`${CONSENT_COOKIE_NAME}=`))
  if (!match) return null
  return parseConsentCookie(match.split("=", 2)[1])
}

function writeCookieClient(value: ConsentCategories) {
  if (typeof document === "undefined") return
  document.cookie = serializeConsentCookie(value)
}

export function CookieConsentProvider({
  children,
  initialConsent,
}: {
  children: React.ReactNode
  // Server-rendered initial value so the banner's visibility is correct
  // from the very first SSR'd byte — avoids the one-frame flash that a
  // client-only `useEffect` cookie read would produce for return visitors.
  initialConsent: ConsentCategories | null
}) {
  const [consent, setConsent] = useState<ConsentCategories | null>(
    () => initialConsent ?? readCookieClient()
  )
  const [isPreferencesOpen, setPreferencesOpen] = useState(false)

  const apply = useCallback((next: ConsentCategories) => {
    writeCookieClient(next)
    setConsent(next)
  }, [])

  const value = useMemo<CookieConsentContextValue>(
    () => ({
      consent,
      hasDecided: consent !== null,
      acceptAll: () => {
        apply(DEFAULT_ACCEPTED)
        setPreferencesOpen(false)
      },
      rejectAll: () => {
        apply(DEFAULT_REJECTED)
        setPreferencesOpen(false)
      },
      savePreferences: ({ analytics, marketing }) => {
        apply({ essential: true, analytics, marketing })
        setPreferencesOpen(false)
      },
      openPreferences: () => setPreferencesOpen(true),
      closePreferences: () => setPreferencesOpen(false),
      isPreferencesOpen,
    }),
    [consent, isPreferencesOpen, apply]
  )

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  )
}

export function useCookieConsent(): CookieConsentContextValue {
  const ctx = useContext(CookieConsentContext)
  if (!ctx) {
    throw new Error(
      "useCookieConsent must be used inside a <CookieConsentProvider>"
    )
  }
  return ctx
}
